/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import type { SelectItem } from '@mantine/core';
import { Select, createStyles, useMantineTheme } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';
import { IconLock } from '@tabler/icons-react';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';

import type { CellComponentProps, CellDescriptor } from '../../types/common';
import useAlignStyle from '../../useAlignStyle';
import { getDependenciesValues } from '../../../../../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../../../../../context/utils/getIsDependenciesSatisfied';

import type { TableCellDataSelectType, TableColumnSelectType } from './types';

const useStyles = createStyles(() => ({
  cellWrapper: {
    display: 'flex',
  },
  checkboxLabel: {
    whiteSpace: 'nowrap',
  },
  lock: {
    display: 'flex',
    alignItems: 'center',
  },
}));

function TableCellSelect({
  column,
  row,
  cell,
  value,
  allValues,
  sectionKey,
  onChange,
  allErrors,
}: CellComponentProps<TableColumnSelectType, TableCellDataSelectType>) {
  const theme = useMantineTheme();
  const [isCellDisabled, setCellDisabled] = useState(false);
  const { classes, cx } = useStyles();
  const { classes: alignClasses } = useAlignStyle();

  const filteredOptions = useMemo(
    () =>
      cell.options.filter((option) => {
        if (!option.dependsOn) {
          return true;
        }

        const dependsOn = option.dependsOn;
        const dependencyValues = getDependenciesValues(allValues, sectionKey, dependsOn);
        const isDependencySatisfied = getIsDependenciesSatisfied(
          row.key,
          dependsOn,
          allErrors,
          dependencyValues
        );

        return isDependencySatisfied;
      }),
    [JSON.stringify(allErrors), JSON.stringify(allValues), cell.options, row.key, sectionKey]
  );

  useEffect(() => {
    if (!cell.disabledWhen) return;

    const dependencyValues = getDependenciesValues(allValues, sectionKey, cell.disabledWhen);
    const isDependencySatisfied = getIsDependenciesSatisfied(
      row.key,
      cell.disabledWhen,
      allErrors,
      dependencyValues
    );

    setCellDisabled(isDependencySatisfied);
  }, [
    JSON.stringify(allErrors),
    JSON.stringify(allValues),
    cell.disabledWhen,
    sectionKey,
    row.key,
  ]);

  useEffect(() => {
    if (typeof cell.defaultValue === 'undefined') {
      return;
    }
    if (typeof value !== 'undefined') {
      const isInOptions = filteredOptions.some((option) => option.value === value);
      if (!isInOptions) {
        onChange(filteredOptions[0]?.value ?? cell.defaultValue, true);
      }
      return;
    }
    if (filteredOptions.length === 0) {
      return;
    }

    onChange(cell.defaultValue, true);
  }, [JSON.stringify(filteredOptions), cell.defaultValue, onChange, value]);

  return (
    <div
      className={cx(classes.cellWrapper, {
        [alignClasses.start]: column.params?.align === 'start',
        [alignClasses.center]: column.params?.align === 'center',
        [alignClasses.end]: column.params?.align === 'end',
      })}
    >
      <Tooltip
        isRendered={!!cell.params?.tooltip}
        withinPortal
        position="top-start"
        key={`${row.key}${column.key}`}
        label={cell.params?.tooltip}
      >
        {cell.isDisabled || isCellDisabled ? (
          <div className={classes.lock}>
            <IconLock stroke={theme.colors.gray2[7]} />
          </div>
        ) : (
          <Select
            data={
              filteredOptions.map((option) => ({
                label: option.label,
                value: option.value,
              })) as SelectItem[]
            }
            value={value as string}
            onChange={onChange}
            wrapperProps={{ 'data-test-id': dt.table.body.select }}
            disabled={cell.isDisabled || isCellDisabled}
          />
        )}
      </Tooltip>
    </div>
  );
}

const descriptor: CellDescriptor<TableColumnSelectType, TableCellDataSelectType> = {
  name: 'Select',
  Cell: TableCellSelect,
  createValidationSchema(cell) {
    let schema: ZodTypeAny = z.union([z.string(), z.number(), z.boolean()]);
    if (cell.validation?.isOptional) {
      schema = schema.optional();
    }

    return {
      schema,
    };
  },
};

export default descriptor;
